import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Add from '@mui/icons-material/Add'
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import ListTableContainer from '../../component/ListTableContainer'
import ListEmptyState from '../../component/ListEmptyState'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip from '../../component/StatusChip'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget } from '../../utils/listPageState'
import { renderDateRange } from '../../utils/dateDisplay'
import { formatNumber } from '../../utils/numberDisplay'

const DEFAULT_SEARCH_SELECT = {
    searchMeetType2: '',
    searchStatus: '',
}

const SEARCH_SELECT_PARAMS = {
    searchMeetType2: {
        param: 'type',
        defaultValue: DEFAULT_SEARCH_SELECT.searchMeetType2,
    },
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
    },
}

const getInitialListState = (location) => {
    return createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => {
    return buildListSearchQuery({
        curPage,
        searchQuery,
        searchSelect,
        selectParams: SEARCH_SELECT_PARAMS,
    })
}

const ANIMAL_TABLE_COLUMNS = [
    { width: 64 },
    { width: 68 },
    { width: 280 },
    { width: 110 },
    { width: 86 },
    { width: 90 },
    { width: 70 },
    { width: 70 },
    { width: 90 },
    { width: 90 },
    { width: 122 },
    { width: 122 },
    { width: 76 },
    { width: 96 },
]

const ANIMAL_TABLE_MIN_WIDTH = ANIMAL_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const ANIMAL_TABLE_SX = {
    minWidth: ANIMAL_TABLE_MIN_WIDTH,
    tableLayout: 'fixed',
    '& th': {
        whiteSpace: 'nowrap',
    },
    '& td': {
        verticalAlign: 'middle',
    },
    '& tbody tr': {
        height: 58,
    },
}

const ANIMAL_TITLE_TEXT_SX = {
    display: '-webkit-box',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    lineHeight: 1.45,
    wordBreak: 'break-word',
}

const ANIMAL_NOWRAP_CELL_SX = {
    whiteSpace: 'nowrap',
}

const ANIMAL_DATE_CELL_SX = {
    whiteSpace: 'nowrap',
    lineHeight: 1.45,
}

const getAnimalListTotalCount = (count) => {
    if (Array.isArray(count)) {
        return count.length
    }

    return Number(count || 0)
}

const animalThumbnailPlaceholderSx = {
    width: 32,
    height: 32,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.default',
    color: 'text.disabled',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    verticalAlign: 'middle',
}

const getAnimalThumbnailSrc = (row) => {
    const thumbnail = row.thumbnail ? String(row.thumbnail) : ''

    if (!thumbnail) {
        return ''
    }

    if (thumbnail.includes('animal')) {
        return `${process.env.REACT_APP_AWS_S3_URL}/${thumbnail}`
    }

    if (!row.adminId) {
        return ''
    }

    return `${process.env.REACT_APP_SERVER_URL}/product/${row.adminId}/${thumbnail}`
}

const renderAnimalThumbnail = (row) => {
    const thumbnailSrc = getAnimalThumbnailSrc(row)

    if (!thumbnailSrc) {
        return (
            <Box sx={animalThumbnailPlaceholderSx} aria-label="애니멀런 이미지 없음">
                <ImageNotSupportedIcon sx={{ fontSize: 18 }} />
            </Box>
        )
    }

    return <img className="listImg" src={thumbnailSrc} alt={row.title || '애니멀런 이미지'} />
}

const renderAnimalTitle = (title) => {
    const titleText = title ? String(title) : '-'

    return (
        <Box component="span" title={titleText} sx={ANIMAL_TITLE_TEXT_SX}>
            {titleText}
        </Box>
    )
}

const getAnimalMeetTypeLabel = (meetType2) => {
    if (Number(meetType2) === 1) {
        return '애니멀런'
    }

    if (Number(meetType2) === 2) {
        return '댕댕이레이스'
    }

    if (Number(meetType2) === 3) {
        return '산넘어산'
    }

    return 'B급마라톤'
}

const renderDdayChip = (dDay) => {
    const day = Number(dDay)

    if (!Number.isFinite(day)) {
        return <StatusChip label="-" variant="outlined" />
    }

    if (day > 0) {
        return <StatusChip label={`D+${day}`} variant="outlined" />
    }

    if (day === 0) {
        return <StatusChip label="D-Day" color="warning" />
    }

    return <StatusChip label={`D${day}`} color="info" />
}

const renderAvailabilityChip = (status) => {
    if (Number(status) === 1) {
        return <StatusChip label="가능" color="success" />
    }

    return <StatusChip label="불가" variant="outlined" />
}

const renderVisibilityChip = (isShow) => {
    if (Number(isShow) === 0) {
        return <StatusChip label="숨김" variant="outlined" />
    }

    return <StatusChip label="노출" color="success" />
}

export default function Animals({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [data, setData] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const deleteInFlightRef = useRef(false)
    const silentListRefreshRef = useRef(false)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/animal/getAnimalList', {
                page: curPage, offset: offset, adminId: adminId, role: role, searchText: searchQuery, searchSelect: searchSelect
            })

            if (data.code === 1) {
                setTotalCount(getAnimalListTotalCount(data.data?.count))
                setData(data.data?.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '애니멀런 목록을 불러오지 못했습니다.'

                setTotalCount(0)
                setData([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '애니멀런 목록을 불러오지 못했습니다.'

            setTotalCount(0)
            setData([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [adminId, curPage, history, role, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        const { name, value } = e.target

        if (actionLoading) {
            return
        }

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        setCurPage(1)
        setSearchQuery((searchText || '').trim())
        setSearchSelect((prevSearchSelect) => ({
            ...prevSearchSelect,
            [name]: value
        }))
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (actionLoading) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (actionLoading) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
            return
        }

        if (searchQuery !== trimmedSearchText) {
            return
        }

        getData(false)
    }

    const handleDelete = (id) => {
        if (deleteInFlightRef.current) {
            return
        }

        deleteInFlightRef.current = true
        setActionLoading(true)

        const deleteData = async () => {
            try {
                const { data: response } = await API.post('/api/v1/admin/animal/delete', { productId: id })

                if (response.code === 1) {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('success')
                    setMessageSnackbar(response.message)

                    if (data.length === 1 && curPage > 1) {
                        setCurPage((prev) => prev - 1)
                        return
                    }

                    await getData(false)
                } else if (response.code === 403) {
                    history.push('/login')
                } else {
                    setOpenSnackbar(true)
                    setSeveritySnackbar('error')
                    setMessageSnackbar(response.message)
                }
            } catch (_error) {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar('애니멀런을 삭제하지 못했습니다.')
            } finally {
                deleteInFlightRef.current = false
                setActionLoading(false)
            }
        }
        deleteData()
    }

    const getAvailableQuantity = (row) => {
        const limitQuantity = Number(row.payQuantity || 0)
        const netSoldQuantity = Number(row.netSoldQuantity || 0)
        const waitingDepositQuantity = Number(row.waitingDepositQuantity || 0)
        return Math.max(limitQuantity - netSoldQuantity - waitingDepositQuantity, 0)
    }

    // 검색어와 선택 필터가 기본값에서 벗어났는지 확인해 빈 상태 안내를 구분한다.
    const hasActiveFilters = () => {
        if ((searchQuery || '').trim()) {
            return true
        }

        return Object.entries(DEFAULT_SEARCH_SELECT).some(([key, defaultValue]) => {
            const currentValue = searchSelect?.[key] ?? defaultValue

            return String(currentValue) !== String(defaultValue)
        })
    }

    // 목록 행이 없을 때 오류, 필터 결과 없음, 기본 빈 목록을 명확히 보여준다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="애니멀런 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button type="button" size="small" variant="outlined" onClick={() => getData(false)} disabled={actionLoading}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters()) {
            return (
                <ListEmptyState
                    title="조건에 맞는 애니멀런이 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={actionLoading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 애니멀런이 없습니다."
                description="애니멀런을 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Grid item xs={12}>
                    <Spiner/>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Grid>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>애니멀런 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={actionLoading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="타이틀"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                disabled={actionLoading}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={actionLoading}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight:2 }} orientation="vertical" />
                            <TextField
                                label="타입"
                                select
                                name="searchMeetType2"
                                value={searchSelect.searchMeetType2}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={actionLoading}
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                <MenuItem value='1'>애니멀런</MenuItem>
                                <MenuItem value='2'>댕댕이레이스</MenuItem>
                                <MenuItem value='3'>산넘어산</MenuItem>
                                <MenuItem value='4'>B급마라톤</MenuItem>
                            </TextField>
                            {/* <TextField
                                label="상태"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus}
                                onChange={updateSearchSelect}
                                size="small"
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                <MenuItem value='3'>판매</MenuItem>
                                <MenuItem value='4'>종료</MenuItem>
                            </TextField> */}
                        </ListFilterForm>

                    </ListFilterBar>
                    
                    {/* 테이블 */}
                    <ListTableToolbar
                        right={(
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-animal',
                                    search: location.search,
                                    curPage: curPage,
                                    searchText: searchQuery,
                                    searchSelect: searchSelect
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                    <Table size="small" sx={ANIMAL_TABLE_SX}>
                        <colgroup>
                            {ANIMAL_TABLE_COLUMNS.map((column, index) => (
                                <col key={`animal-column-${index}`} style={{ width: column.width }} />
                            ))}
                        </colgroup>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>이미지</TableCell>
                                <TableCell>타이틀</TableCell>
                                <TableCell>타입</TableCell>
                                <TableCell align="left">D-Day</TableCell>
                                <TableCell align="center">가격</TableCell>
                                <TableCell align="center">구매</TableCell>
                                <TableCell align="center">쿠폰</TableCell>
                                <TableCell align="center">판매제한</TableCell>
                                <TableCell align="center">판매가능</TableCell>
                                <TableCell>접수일</TableCell>
                                <TableCell>대회일</TableCell>
                                <TableCell align="center">노출</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.length > 0 ? data.map((row) => (
                                <TableRow key={row.productId} sx={{ backgroundColor: Number(row.isShow) === 0 ? 'action.hover' : 'inherit' }}>
                                    <TableCell sx={ANIMAL_NOWRAP_CELL_SX}>{row.rownum}</TableCell>
                                    <TableCell sx={ANIMAL_NOWRAP_CELL_SX}>{renderAnimalThumbnail(row)}</TableCell>
                                    <TableCell>{renderAnimalTitle(row.title)}</TableCell>
                                    <TableCell sx={ANIMAL_NOWRAP_CELL_SX}>{getAnimalMeetTypeLabel(row.meetType2)}</TableCell>
                                    <TableCell align="left" sx={ANIMAL_NOWRAP_CELL_SX}>{renderDdayChip(row.dDay)}</TableCell>
                                    <TableCell align="right" sx={ANIMAL_NOWRAP_CELL_SX}>{formatNumber(row.price)} ~</TableCell>
                                    <TableCell align="center" sx={ANIMAL_NOWRAP_CELL_SX}>{renderAvailabilityChip(row.paymentStatus)}</TableCell>
                                    <TableCell align="center" sx={ANIMAL_NOWRAP_CELL_SX}>{renderAvailabilityChip(row.couponStatus)}</TableCell>
                                    <TableCell align="center" sx={ANIMAL_NOWRAP_CELL_SX}>{formatNumber(row.payQuantity)}</TableCell>
                                    <TableCell align="center" sx={ANIMAL_NOWRAP_CELL_SX}>{formatNumber(getAvailableQuantity(row))}</TableCell>
                                    <TableCell sx={ANIMAL_DATE_CELL_SX}>{renderDateRange(row.startTime, row.endTime)}</TableCell>
                                    <TableCell sx={ANIMAL_DATE_CELL_SX}>{renderDateRange(row.beginTime, row.finishTime)}</TableCell>
                                    <TableCell align="center" sx={ANIMAL_NOWRAP_CELL_SX}>{renderVisibilityChip(row.isShow)}</TableCell>
                                    <TableCell align="right" sx={ANIMAL_NOWRAP_CELL_SX}>
                                        <ListActionGroup disabled={actionLoading}>
                                            <EditIcon path={'animal'} id={row.productId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect}/>
                                            <DeleteAlertDialog handleDelete={handleDelete} id={row.productId}/>
                                        </ListActionGroup>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={14}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
